import React, { FormEvent, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, Calendar, FileText, XCircle, Upload, LoaderCircle } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  UpdatePurchaseOrderConfirmationFormDataKey,
  POProductConfirmationInputField,
  UpdatePurchaseOrderConfirmationFormData,
  PurchaseOrder,
  UpdatePurchaseOrderConfirmationFormAction
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { OrderProductConfirmationInputTable } from "src/components/tables/inputTables";
import { DateInputBox, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { PercentageBar } from "src/components/ui/percentageBar";
import { ControlButton } from "src/components/tables";
import {
  parseConfirmationUploadXlsx,
  applyConfirmationUploadResults,
  type UploadProductRowResult
} from "src/utils/confirmationUploadParser";

interface UpdatePurchaseOrderConfirmationFormProps {
  formData: UpdatePurchaseOrderConfirmationFormData;
  purchaseOrderToUpdate?: PurchaseOrder;
  purchaseOrderOptions?: string[];
  validationErrors?: ValidationErrors<UpdatePurchaseOrderConfirmationFormDataKey>;
  formLvlErrorMessage: string;
  isFormUpdated: boolean;
  isFormComplete: boolean;
  fulfilmentRate: number;
  handleInputChange: (
    field: UpdatePurchaseOrderConfirmationFormDataKey,
    value: UpdatePurchaseOrderConfirmationFormData[keyof UpdatePurchaseOrderConfirmationFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: UpdatePurchaseOrderConfirmationFormAction) => void;
}

const UpdatePurchaseOrderConfirmationForm: React.FC<UpdatePurchaseOrderConfirmationFormProps> = ({
  formData,
  purchaseOrderToUpdate,
  purchaseOrderOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  isFormComplete,
  fulfilmentRate,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingProducts, setIsUploadingProducts] = useState(false);
  const [uploadExtraProductIds, setUploadExtraProductIds] = useState<string[]>([]);
  const [uploadRowErrors, setUploadRowErrors] = useState<UploadProductRowResult[]>([]);
  const [uploadHeaderError, setUploadHeaderError] = useState<string>("");

  const handleSetProductConfirmations: React.Dispatch<React.SetStateAction<POProductConfirmationInputField[]>> = (
    updatedProductConfirmations
  ) => {
    handleInputChange("productConfirmations", updatedProductConfirmations as POProductConfirmationInputField[]);
  };

  const handleFulfilAllProducts = async () => {
    handleInputChange(
      "productConfirmations",
      formData.productConfirmations.map((productConfirmation) => ({
        ...productConfirmation,
        confirmedQty: productConfirmation.orderQty,
        estRestockDate: "",
        isDiscontinued: false
      }))
    );
  };

  const handleUploadProductsFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !purchaseOrderToUpdate) return;
    setUploadHeaderError("");
    setUploadExtraProductIds([]);
    setUploadRowErrors([]);
    setIsUploadingProducts(true);
    try {
      const productConfirmations = formData.productConfirmations;
      const validIds = new Set(productConfirmations.map((pc) => String(pc.productId).trim()));
      const result = await parseConfirmationUploadXlsx(file, validIds);
      if (result.headerErrors.length > 0) {
        setUploadHeaderError(result.headerErrors.join(" "));
        return;
      }
      if (result.extraProductIds.length > 0) {
        setUploadExtraProductIds(result.extraProductIds);
        return;
      }
      const withErrors = result.rowResults.filter((r) => r.errors.length > 0);
      setUploadRowErrors(withErrors);
      const updated = applyConfirmationUploadResults(formData.productConfirmations, result.rowResults);
      handleInputChange("productConfirmations", updated);
      const appliedCount = result.rowResults.filter((r) => r.errors.length === 0).length;
      if (appliedCount > 0) {
        toast.success(
          withErrors.length > 0
            ? `Applied ${appliedCount} product(s). ${withErrors.length} row(s) had validation errors.`
            : `Applied confirmation data for ${appliedCount} product(s).`
        );
      }
    } catch (err) {
      setUploadHeaderError(err instanceof Error ? err.message : "Failed to process file.");
    } finally {
      setIsUploadingProducts(false);
    }
  };

  const handleUploadProductsClick = () => {
    setUploadHeaderError("");
    setUploadExtraProductIds([]);
    setUploadRowErrors([]);
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Update Confirmation</h1>
            <p className="text-gray-300 mt-2 text-lg">Confirm purchase order details and delivery dates</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                  dropDownOptions={purchaseOrderOptions}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {purchaseOrderToUpdate && (
                <>
                  {/* Product Confirmations Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Product Confirmations
                    </h3>
                    <OrderProductConfirmationInputTable
                      orderProductConfirmations={formData.productConfirmations}
                      setOrderProductConfirmations={handleSetProductConfirmations}
                      vendorLocation={purchaseOrderToUpdate?.origin}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("productConfirmations")}
                    />
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <ControlButton onClick={handleFulfilAllProducts} text="Fulfil All" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={handleUploadProductsFile}
                        aria-hidden
                      />
                      <ControlButton
                        onClick={handleUploadProductsClick}
                        text="Upload Products"
                        icon={<Upload className="h-4 w-4" />}
                        disabled={isUploadingProducts}
                      />
                      {isUploadingProducts && (
                        <span className="inline-flex items-center gap-2 text-sm text-secondary-600">
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                          Processing file...
                        </span>
                      )}
                    </div>
                    {uploadHeaderError && (
                      <div className="mt-3 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-800">
                        {uploadHeaderError}
                      </div>
                    )}
                    {uploadExtraProductIds.length > 0 && (
                      <div className="mt-3 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-800">
                        <p className="font-medium">
                          The file contains product IDs that are not in this purchase order:
                        </p>
                        <p className="mt-1 font-mono text-xs">{uploadExtraProductIds.join(", ")}</p>
                        <p className="mt-2 text-error-700">
                          Please remove these rows or use the correct products file.
                        </p>
                      </div>
                    )}
                    {uploadRowErrors.length > 0 && (
                      <div className="mt-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800">
                        <p className="font-medium">Some rows were not applied due to validation errors:</p>
                        <ul className="mt-2 list-inside list-disc space-y-1 text-xs">
                          {uploadRowErrors.map((r) => (
                            <li key={r.productId}>
                              <span className="font-mono">{r.productId}</span>: {r.errors.join("; ")}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-warning-700">Other products were updated successfully.</p>
                      </div>
                    )}

                    {/* Fulfilment Rate */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fulfilment Rate</label>
                      <PercentageBar percentage={fulfilmentRate} width="65%" />
                    </div>
                  </div>

                  {/* Delivery Dates Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {purchaseOrderToUpdate.collectionOrder ? "Collection Date" : "Delivery Dates"}
                    </h3>

                    {purchaseOrderToUpdate.collectionOrder ? (
                      <div>
                        <DateInputBox
                          id="est-collection-date"
                          label="Estimate Collection Date"
                          onChange={(value) => handleInputChange("estCollectionDate", value)}
                          value={formData.estCollectionDate}
                          tooltipMessage="Please ship at your earliest convenience."
                        />
                        <ValidationErrorMessage
                          message={validationErrors?.getErrorMessageByFormDataKey("estCollectionDate")}
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <DateInputBox
                            id="est-ship-date"
                            label="Estimate Ship Date"
                            onChange={(value) => handleInputChange("estShipDate", value)}
                            value={formData.estShipDate}
                            tooltipMessage="Please ship at your earliest convenience."
                          />
                          <ValidationErrorMessage
                            message={validationErrors?.getErrorMessageByFormDataKey("estShipDate")}
                          />
                        </div>
                        <div>
                          <DateInputBox
                            id="est-delivery-date"
                            label="Estimate Delivery Date"
                            onChange={(value) => handleInputChange("estDeliveryDate", value)}
                            value={formData.estDeliveryDate}
                            tooltipMessage="Please ship at your earliest convenience."
                          />
                          <ValidationErrorMessage
                            message={validationErrors?.getErrorMessageByFormDataKey("estDeliveryDate")}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Additional Notes Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase">
                      Additional Information
                    </h3>
                    <TextInputBoxLarge
                      id="confirmation-note-body"
                      placeholder="Confirmation Note (Optional)"
                      onChange={(value) => handleInputChange("additionalInfo", value)}
                      value={formData.additionalInfo}
                      maxLength={1000}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("additionalInfo")}
                    />
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                  onClick={() => setAction(!purchaseOrderToUpdate ? "FETCH" : "UPDATE")}
                  type="submit"
                >
                  {!purchaseOrderToUpdate ? "Search" : "Update Confirmation"}
                </button>
              </div>

              {/* Cancellation Section */}
              {purchaseOrderToUpdate && (
                <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                  <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Purchase Order
                  </h3>
                  <TextInputBoxLarge
                    id="cancellation-reason"
                    placeholder="Cancellation Reason"
                    onChange={(value) => handleInputChange("cancellationReason", value)}
                    value={formData.cancellationReason ?? ""}
                    maxLength={650}
                  />
                  <ValidationErrorMessage
                    message={validationErrors?.getErrorMessageByFormDataKey("cancellationReason")}
                  />
                  <div className="mt-4">
                    <button
                      className="px-6 py-3 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                      onClick={() => setAction("CANCEL_CONFIRMATION")}
                      type="submit"
                      disabled={!formData.cancellationReason}
                    >
                      Reject Purchase Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { UpdatePurchaseOrderConfirmationForm };
